import React, { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import SegmentApi from 'services/KjederegisteretAdminApi/SegmentApi';
import { SegmentForslag } from 'services/KjederegisteretAdminApi/SegmentApi/types';
import useDebounce from 'utils/hooks/useDebounce';
import { segmentRegler } from './regler';
import useSegmenterPerBransje from './useSegmenterPerBransje';
import { getSegmenterForBransje, getVisibleForslag, shouldClearSegment } from './utils';

interface Props {
  bransjeId?: number;
  /** Brukes til å hente forslag for eksisterende enhet. */
  enhetId?: number;
  /** Grunnlag for forslag når enheten ikke er opprettet ennå (uten enhetId). */
  enhetnavn?: string;
  juridisknavn?: string;
  markedskjedeNr?: number | null;
  selected?: string | null;
  disabled?: boolean;
  onChange: (segment: string | undefined) => void;
}

const SegmentPicker: React.FC<Props> = ({
  bransjeId,
  enhetId,
  enhetnavn,
  juridisknavn,
  markedskjedeNr,
  selected,
  disabled,
  onChange,
}) => {
  const segmenter = useSegmenterPerBransje();
  const [forslag, setForslag] = useState<SegmentForslag>();
  const [showRegler, setShowRegler] = useState(false);

  const debouncedEnhetnavn = useDebounce(enhetnavn);
  const debouncedJuridisknavn = useDebounce(juridisknavn);
  const debouncedMarkedskjedeNr = useDebounce(markedskjedeNr);

  const tilgjengelige = getSegmenterForBransje(segmenter, bransjeId);

  useEffect(() => {
    if (shouldClearSegment(segmenter, bransjeId, selected)) {
      onChange(undefined);
    }
  }, [segmenter, bransjeId, selected]);

  const harSegmenter = tilgjengelige.length > 0;
  useEffect(() => {
    const harNavn = !!debouncedEnhetnavn || !!debouncedJuridisknavn;
    if (!harSegmenter || (!enhetId && !harNavn)) {
      setForslag(undefined);
      return;
    }
    let isSubscribed = true;
    const henting = enhetId
      ? SegmentApi.getForslag(enhetId)
      : SegmentApi.getForslagForNyEnhet({
          enhetnavn: debouncedEnhetnavn,
          juridisknavn: debouncedJuridisknavn,
          markedskjedeNr: debouncedMarkedskjedeNr,
        });
    henting
      .then((data) => isSubscribed && setForslag(data))
      .catch(() => isSubscribed && setForslag(undefined));
    return () => {
      isSubscribed = false;
    };
  }, [enhetId, harSegmenter, debouncedEnhetnavn, debouncedJuridisknavn, debouncedMarkedskjedeNr]);

  if (!harSegmenter) return null;

  const synligForslag = getVisibleForslag(forslag, tilgjengelige);
  const erValgt = synligForslag?.segment === selected;

  return (
    <>
      <Form.Control
        as="select"
        id="segment-picker"
        disabled={disabled}
        value={selected ?? ''}
        onChange={(e) => onChange(e.target.value || undefined)}
      >
        <option value="">Velg segment...</option>
        {tilgjengelige.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </Form.Control>
      {forslag?.muligFeilBransje && (
        <Form.Text className="text-warning d-block">
          Mulig feil bransje: {forslag.begrunnelse}
        </Form.Text>
      )}
      {synligForslag && !synligForslag.muligFeilBransje && (
        <Form.Text className="d-block">
          Foreslått: <strong>{synligForslag.segment}</strong>
          {synligForslag.konfidens === 'Lav' && ' (usikkert – verifiser)'}{' '}
          {!erValgt && (
            <Button
              variant="link"
              size="sm"
              className="p-0 align-baseline"
              disabled={disabled}
              onClick={() => onChange(synligForslag.segment ?? undefined)}
            >
              Bruk
            </Button>
          )}
          <br />
          <span title={synligForslag.begrunnelse}>{synligForslag.begrunnelse}</span>{' '}
          <Button
            variant="link"
            size="sm"
            className="p-0 align-baseline"
            onClick={() => setShowRegler(true)}
          >
            Se segmentregler
          </Button>
        </Form.Text>
      )}
      {(!synligForslag || synligForslag.muligFeilBransje) && (
        <Form.Text className="d-block">
          <Button
            variant="link"
            size="sm"
            className="p-0 align-baseline"
            onClick={() => setShowRegler(true)}
          >
            Se segmentregler
          </Button>
        </Form.Text>
      )}
      <Modal show={showRegler} onHide={() => setShowRegler(false)} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Segmentregler – Institusjonsservering</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {segmentRegler.map((r) => (
            <div key={r.segment} className="mb-3">
              <h6>{r.segment}</h6>
              <ul className="mb-0">
                {r.kriterier.map((k) => (
                  <li key={k}>{k}</li>
                ))}
              </ul>
            </div>
          ))}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default SegmentPicker;
